import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
    DataGrid,
    GridToolbar,
    GridRowModes,
    useGridApiRef,
} from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import {
    IconButton,
    Checkbox,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Chip,
    Popover,
    Button,
    useMediaQuery,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { format } from 'date-fns';
import { FitColumnsButton, useFitTableColumns } from '../../../hooks/useFitTableColumns';
import {
    getGridStringOperatorsWithComparison,
    getGridDateOperatorsWithNotLike,
} from '../../../Utils/DataGridFilterOperators';
import NoDocumentScanned from '../../../assests/images/svgs/NoDocumentScanned.svg';
import DocumentItemIcon from '../../../assests/images/Icons/documentItem.svg';
import editIcon from '../../../assests/images/Icons/editIcon.svg';
import deleteIcon from '../../../assests/images/Icons/deleteIcon.svg';
import Constants from '../../../assests/Constants/constants';
import styles from './OtherDocumentDataGrid.module.scss';

const STORAGE_KEY_VIEW = 'otherDocumentDataGrid_view';
const STORAGE_KEY_COLUMN_WIDTHS = 'otherDocumentDataGrid_columnWidths';
const STORAGE_KEY_COLUMN_VISIBILITY = 'otherDocumentDataGrid_columnVisibility';
const STORAGE_KEY_COLUMN_VISIBILITY_CUSTOM = 'otherDocumentDataGrid_columnVisibility_custom';
const TABLE_VIEWS = ['compact', 'standard', 'full', 'custom'];
const VIEW_INCLUDES_TIER = {
    compact: ['priority', 'compact'],
    standard: ['priority', 'compact', 'standard'],
    full: ['priority', 'compact', 'standard', 'full'],
    custom: ['priority', 'compact', 'standard', 'full'],
};

const FIT_WEIGHTS = {
    document: 96,
    processedBy: 110,
    processed: 92,
    title: 160,
    documentType: 130,
    sectionTitle: 130,
    documentDate: 100,
    uploadDate: 100,
    uploadNumber: 84,
    actions: 96,
};

const TABLE_VIEW_MENU_PROPS = {
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: '8px',
            border: '1px solid #e5e7f6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            minWidth: 'var(--xtram-menu-width)',
            '& .MuiList-root': { p: 0 },
            '& .MuiMenuItem-root': {
                minHeight: 'var(--xtram-menu-item-height)',
                px: 'var(--xtram-menu-item-pad-x)',
                py: 'var(--xtram-menu-item-pad-y)',
                fontSize: 'var(--xtram-text-sm)',
                fontWeight: 400,
                lineHeight: 1.35,
                color: '#696665',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                '&:not(:last-of-type)': { borderBottom: '1px solid #f0f0f0' },
                '&:hover': { backgroundColor: '#304fff', color: '#ffffff' },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(48, 79, 255, 0.1)',
                    color: '#304fff',
                    fontWeight: 500,
                },
                '&.Mui-selected:hover': { backgroundColor: '#304fff', color: '#ffffff' },
            },
        },
    },
};

const COLUMN_PANEL_PAPER_SX = {
    p: 0,
    mt: 0.5,
    maxHeight: 360,
    width: 'var(--xtram-menu-width)',
    minWidth: 'var(--xtram-menu-width)',
    maxWidth: 'var(--xtram-menu-width)',
    borderRadius: '8px',
    border: '1px solid #e5e7f6',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
};

const StyledDataGrid = styled(DataGrid)(() => ({
    width: '100%',
    '& *': {
        boxSizing: 'border-box !important',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#E3E7FE ',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        lineHeight: '42px',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#E3E7FE ',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 8px',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        height: '42px !important',
        boxSizing: 'border-box',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#1222b3',
        fontFamily: 'Montserrat',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 'normal',
    },
    '& .MuiDataGrid-sortIcon': {
        opacity: '0.8 !important',
        color: '#1222b3',
    },
    '& .MuiDataGrid-menuIconButton': {
        color: '#1222b3',
    },
    '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
        flexDirection: 'row',
    },
    '& .MuiDataGrid-cell--textRight': {
        textAlign: 'left',
        justifyContent: 'flex-start',
    },
    '& .MuiDataGrid-main, & .MuiDataGrid-overlayWrapper': {
        backgroundColor: '#ffffff',
    },
    '& .MuiDataGrid-virtualScroller': {
        backgroundColor: '#ffffff',
        overflowX: 'auto',
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-cell': {
        borderTop: '1px solid rgba(191, 191, 193, 0.56)',
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.3,
        overflow: 'hidden',
        minHeight: '50px',
        maxHeight: '90px',
    },
    '& .MuiDataGrid-row': {
        minHeight: '50px',
        maxHeight: '90px !important',
    },
    '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell > span': {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 4,
        overflow: 'hidden',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maxWidth: '100%',
        lineHeight: 1.3,
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'rgba(227, 230, 255, 0.3)',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '&.fit-to-screen .MuiDataGrid-columnHeader': {
        whiteSpace: 'nowrap',
        padding: '0 4px',
    },
    '&.fit-to-screen .MuiDataGrid-columnHeaderTitleContainer': {
        overflow: 'hidden',
    },
    '&.fit-to-screen .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    '&.fit-to-screen .MuiDataGrid-cell': {
        whiteSpace: 'nowrap',
        wordBreak: 'normal',
        overflowWrap: 'normal',
        lineHeight: 1.2,
        minHeight: '44px',
        maxHeight: '48px',
        padding: '0 4px',
    },
    '&.fit-to-screen .MuiDataGrid-row': {
        minHeight: '44px',
        maxHeight: '48px !important',
    },
    '&.fit-to-screen .MuiDataGrid-cellContent, &.fit-to-screen .MuiDataGrid-cell > span, &.fit-to-screen .MuiDataGrid-cell p': {
        display: 'block',
        WebkitLineClamp: 'unset',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        maxWidth: '100%',
        margin: 0,
    },
    '&.fit-to-screen .processed, &.fit-to-screen .unprocessed': {
        height: '22px',
        padding: '0 6px',
        fontSize: '11px',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        overflow: 'hidden',
    },
    '&.fit-to-screen .iframe-container': {
        width: '100%',
        maxWidth: '100%',
    },
    '&.fit-to-screen .iframe-container iframe, &.fit-to-screen .iframe-container img': {
        width: '100% !important',
        maxWidth: '100%',
        height: '32px !important',
        objectFit: 'contain',
    },
}));

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return format(date, 'dd/MM/yyyy');
};

const readJson = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? parsed : fallback;
    } catch {
        return fallback;
    }
};

const OtherDocumentDataGrid = forwardRef((props, ref) => {
    const {
        t,
        data,
        client,
        subAccountantOptions,
        openPdfInNewTab,
        handleEdit,
        handleDeleteClick,
        handleSelectRows,
        direction,
        handleRightClickAction,
        userTypeContext,
        permissionsState,
        hideStatus = false,
        titleNameByType = {},
    } = props;

    const apiRef = useGridApiRef();
    const isTouchPrimary = useMediaQuery('(hover: none), (pointer: coarse)');
    const preferDocIconThumb =
        isTouchPrimary ||
        (typeof navigator !== 'undefined' &&
            (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)));

    const [selectedRows, setSelectedRows] = useState([]);
    const [rows, setRows] = useState(data);
    const [rowModesModel, setRowModesModel] = useState({});
    const [columnsPanelAnchor, setColumnsPanelAnchor] = useState(null);
    const [tableView, setTableView] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY_VIEW);
            return TABLE_VIEWS.includes(saved) ? saved : 'standard';
        } catch {
            return 'standard';
        }
    });
    const [columnWidths, setColumnWidths] = useState(() => readJson(STORAGE_KEY_COLUMN_WIDTHS, {}));
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        try {
            if (localStorage.getItem(STORAGE_KEY_VIEW) === 'custom') {
                return readJson(STORAGE_KEY_COLUMN_VISIBILITY_CUSTOM, {});
            }
        } catch (_) {}
        return readJson(STORAGE_KEY_COLUMN_VISIBILITY, {});
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_VIEW, tableView);
        } catch (_) {}
    }, [tableView]);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_COLUMN_VISIBILITY, JSON.stringify(columnVisibilityModel));
            if (tableView === 'custom') {
                localStorage.setItem(STORAGE_KEY_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
            }
        } catch (_) {}
    }, [columnVisibilityModel, tableView]);

    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    useImperativeHandle(ref, () => ({
        deselectRows(ids) {
            setSelectedRows((prevSelectedRows) =>
                prevSelectedRows.filter((rowId) => !ids.includes(rowId))
            );
        },
    }));

    const stringFilterOperators = useMemo(() => getGridStringOperatorsWithComparison(t), [t]);
    const dateFilterOperators = useMemo(() => getGridDateOperatorsWithNotLike(t), [t]);

    const handleSelectionModelChange = (newSelection) => {
        setSelectedRows(newSelection);
        handleSelectRows(newSelection);
    };

    const processRowUpdate = (newRow) => {
        setRows((prevRows) =>
            prevRows.map((row) => (row.id === newRow.id ? newRow : row))
        );
        return newRow;
    };

    const isClientWithoutPremium = userTypeContext?.toString() === Constants.UserType.Client && !permissionsState.Premium;
    const isCheckboxSelectionEnabled = !isClientWithoutPremium;
    const unprocessedIds = client?.documentIds?.otherDocuments || [];

    const baseColumns = [
        {
            field: 'document',
            headerName: t('Document'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            sortable: false,
            filterable: false,
            viewTier: 'priority',
            renderCell: (params) => {
                const hasDoc = Boolean(params.row.documentUrl);
                const openPreview = (e) => {
                    e?.preventDefault?.();
                    e?.stopPropagation?.();
                    if (!hasDoc) return;
                    openPdfInNewTab(params.row.documentUrl);
                };

                return (
                    <div
                        className={`iframe-container${hasDoc ? '' : ` ${styles.docThumbEmpty}`}`}
                        onClick={openPreview}
                        onPointerDown={(e) => e.stopPropagation()}
                        style={{ cursor: hasDoc ? 'pointer' : 'default', position: 'relative' }}
                        title={params.row.title || t('Document')}
                    >
                        {hasDoc ? (
                            preferDocIconThumb ? (
                                <img
                                    className={styles.docThumbPresent}
                                    src={DocumentItemIcon}
                                    alt={params.row.title || t('Document')}
                                    width="100"
                                    height="50"
                                    draggable={false}
                                />
                            ) : (
                                <>
                                    <div className="iframe-overlay" aria-hidden />
                                    <iframe
                                        className="zoom-iframe"
                                        src={`${params.row.documentUrl}#page=1`}
                                        width="100px"
                                        height="50px"
                                        title={params.row.title || 'No Title'}
                                        style={{ pointerEvents: 'none', border: 'none' }}
                                        tabIndex={-1}
                                    />
                                </>
                            )
                        ) : (
                            <img
                                className={styles.docThumbPlaceholder}
                                src={NoDocumentScanned}
                                alt={t('Document') || 'No document'}
                                width="100"
                                height="50"
                                draggable={false}
                            />
                        )}
                    </div>
                );
            },
        },
        {
            field: 'processedBy',
            headerName: t('DocumentProcessedBy'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            viewTier: 'standard',
            valueGetter: (value) => {
                if (value === '-100') return t('ProcessedBySystem');
                const accountant = subAccountantOptions?.find((option) => option.value === value);
                return accountant ? accountant.label : '-';
            },
        },
        {
            field: 'processed',
            headerName: t('DocumentProcessed'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            viewTier: 'priority',
            valueGetter: (value, row) => (unprocessedIds.includes(row?.id) ? t('unprocessed') : t('processed')),
            renderCell: (params) => {
                const isUnprocessed = unprocessedIds.includes(params.row.id);
                const handleRightClick = (event) => {
                    event.preventDefault();
                    handleRightClickAction(params.row.id, isUnprocessed ? 'done' : 'notDone', params.row);
                };

                return (
                    <p
                        onContextMenu={handleRightClick}
                        className={isUnprocessed ? 'unprocessed' : 'processed'}
                        style={{ cursor: 'context-menu' }}
                    >
                        {isUnprocessed ? t('unprocessed') : t('processed')}
                    </p>
                );
            },
        },
        {
            field: 'title',
            headerName: t('Title'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            viewTier: 'priority',
            filterOperators: stringFilterOperators,
            renderCell: (params) => <span title={params.value || ''}>{params.value || ''}</span>,
        },
        {
            field: 'documentType',
            headerName: t('Document Type'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            viewTier: 'compact',
            filterOperators: stringFilterOperators,
        },
        {
            field: 'sectionTitle',
            headerName: t('OtherDocumentsTitle'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            viewTier: 'standard',
            filterOperators: stringFilterOperators,
            valueGetter: (value, row) => titleNameByType[row?.documentType] || t('DefaultSection'),
        },
        {
            field: 'documentDate',
            headerName: t('Document Date'),
            headerAlign: 'center',
            align: 'center',
            type: 'date',
            flex: 1,
            viewTier: 'compact',
            filterOperators: dateFilterOperators,
            valueGetter: (value) => (value ? new Date(value) : null),
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: 'uploadDate',
            headerName: t('Upload Date'),
            headerAlign: 'center',
            align: 'center',
            type: 'date',
            flex: 1,
            viewTier: 'standard',
            filterOperators: dateFilterOperators,
            valueGetter: (value) => (value ? new Date(value) : null),
            valueFormatter: (value) => formatDate(value),
        },
        {
            field: 'uploadNumber',
            headerName: t('UploadNumber'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            viewTier: 'full',
            renderCell: (params) => <span>{params.value !== undefined && params.value !== null ? params.value : '-'}</span>,
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: t('Actions'),
            headerAlign: 'center',
            align: 'center',
            flex: 1,
            viewTier: 'priority',
            getActions: (params) => {
                const actions = [
                    <IconButton
                        key="edit"
                        onClick={() => {
                            setRowModesModel((prevModel) => ({
                                ...prevModel,
                                [params.id]: { mode: GridRowModes.Edit },
                            }));
                            handleEdit(params.row);
                        }}
                    >
                        <img src={editIcon} alt="Edit" />
                    </IconButton>,
                ];

                if (!isClientWithoutPremium) {
                    actions.push(
                        <IconButton
                            key="delete"
                            onClick={() => handleDeleteClick(params.row.id, params.row.documentType)}
                        >
                            <img src={deleteIcon} alt="Delete" />
                        </IconButton>
                    );
                }

                return actions;
            },
        },
    ].reverse();

    const visibleColumns = useMemo(() => {
        const included = VIEW_INCLUDES_TIER[tableView] || VIEW_INCLUDES_TIER.standard;
        let cols = baseColumns
            .filter((col) => included.includes(col.viewTier))
            .map(({ viewTier, ...col }) => ({
                ...col,
                minWidth: FIT_WEIGHTS[col.field] || col.minWidth || 80,
            }));
        if (hideStatus) {
            cols = cols.filter((col) => col.field !== 'processed');
        }
        return cols;
    }, [baseColumns, tableView, hideStatus]);

    const {
        containerRef: fitContainerRef,
        columnsWithWidths,
        fitEnabled,
        requestFit,
        handleColumnWidthChange,
    } = useFitTableColumns({
        storageKey: 'otherDocumentDataGrid',
        scope: tableView,
        weightMode: 'column',
        columns: visibleColumns,
        visibilityModel: columnVisibilityModel,
        widths: columnWidths[tableView] || {},
        hasCheckbox: isCheckboxSelectionEnabled,
        apiRef,
        onPersist: (flat) => {
            setColumnWidths((prev) => {
                const next = { ...prev, [tableView]: flat };
                try {
                    localStorage.setItem(STORAGE_KEY_COLUMN_WIDTHS, JSON.stringify(next));
                } catch (_) {}
                return next;
            });
        },
    });

    const handleTableViewChange = (newView) => {
        if (newView !== 'custom') {
            try {
                localStorage.setItem(STORAGE_KEY_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
            } catch (_) {}
            setColumnVisibilityModel({});
        } else {
            setColumnVisibilityModel(readJson(STORAGE_KEY_COLUMN_VISIBILITY_CUSTOM, {}));
        }
        setTableView(newView);
    };

    const setAllColumnsVisible = (visible) => {
        const next = {};
        visibleColumns.forEach((c) => { next[c.field] = visible; });
        setColumnVisibilityModel((prev) => ({ ...prev, ...next }));
    };

    const handleCellDoubleClick = (params) => {
        handleEdit(params.row);
    };

    const selectionCount = selectedRows.length;

    return (
        <div
            className={`${direction}`}
            style={{
                width: '100%',
                height: 'calc(100vh - 360px)',
                minHeight: 420,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                className={`${styles.toolbar} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                dir={direction}
                sx={{ flexShrink: 0 }}
            >
                <div className={styles.tableViewGroup}>
                    <FormControl size="small" className={styles.tableViewControl}>
                        <InputLabel id="other-document-view-label">{t('TableView')}</InputLabel>
                        <Select
                            labelId="other-document-view-label"
                            value={tableView}
                            label={t('TableView')}
                            dir={direction}
                            MenuProps={TABLE_VIEW_MENU_PROPS}
                            onChange={(e) => handleTableViewChange(e.target.value)}
                            sx={{
                                height: 'var(--xtram-control-height, 40px)',
                                minHeight: 'var(--xtram-control-height, 40px)',
                                '& .MuiSelect-select': {
                                    textAlign: direction === 'rtl' ? 'left' : 'right',
                                    py: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            <MenuItem dir={direction} value="compact">{t('TableViewCompact')}</MenuItem>
                            <MenuItem dir={direction} value="standard">{t('TableViewStandard')}</MenuItem>
                            <MenuItem dir={direction} value="full">{t('TableViewFull')}</MenuItem>
                            <MenuItem dir={direction} value="custom">{t('TableViewCustom')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewColumnIcon />}
                        onClick={(e) => setColumnsPanelAnchor(e.currentTarget)}
                        className={styles.columnsBtn}
                        sx={{
                            minHeight: 'var(--xtram-control-height, 40px)',
                            height: 'var(--xtram-control-height, 40px)',
                            textTransform: 'none',
                            borderRadius: '10px',
                            borderColor: '#e5e7f6',
                            color: '#304FFF',
                            fontSize: 'var(--xtram-table-view-label, 11px)',
                            fontWeight: 600,
                            background: '#f5f6fd',
                            '&:hover': { borderColor: '#304FFF', background: '#eaedff' },
                        }}
                    >
                        {t('ShowHideColumns')}
                    </Button>
                    <FitColumnsButton active={fitEnabled} onClick={requestFit} />
                </div>
                <Popover
                    open={Boolean(columnsPanelAnchor)}
                    anchorEl={columnsPanelAnchor}
                    onClose={() => setColumnsPanelAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    PaperProps={{ sx: COLUMN_PANEL_PAPER_SX }}
                >
                    {visibleColumns.map((col, index) => {
                        const isChecked = columnVisibilityModel[col.field] !== false;
                        return (
                            <FormControlLabel
                                key={col.field}
                                dir={direction}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={isChecked}
                                        onChange={(e) => {
                                            setColumnVisibilityModel((prev) => ({ ...prev, [col.field]: e.target.checked }));
                                            setTableView('custom');
                                        }}
                                        sx={{
                                            color: isChecked ? '#304FFF' : '#8b93a7',
                                            '&.Mui-checked': { color: '#304FFF' },
                                        }}
                                    />
                                }
                                label={col.headerName || col.field}
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    mx: 0,
                                    py: 0.65,
                                    minHeight: 'var(--xtram-menu-item-height)',
                                    cursor: 'pointer',
                                    borderBottom: index < visibleColumns.length - 1 ? '1px solid #f0f0f0' : 'none',
                                    backgroundColor: isChecked ? 'rgba(48, 79, 255, 0.1)' : 'transparent',
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 'var(--xtram-text-sm)',
                                        fontWeight: isChecked ? 500 : 400,
                                        lineHeight: 1.35,
                                        color: isChecked ? '#304FFF' : '#696665',
                                    },
                                    '&:hover': {
                                        backgroundColor: '#304fff',
                                        '& .MuiFormControlLabel-label': { color: '#ffffff' },
                                    },
                                    '&:hover .MuiCheckbox-root': { color: '#ffffff' },
                                }}
                            />
                        );
                    })}
                    <Box sx={{ px: 1.5, py: 0.75, display: 'flex', gap: 1, borderTop: '1px solid #f0f0f0' }}>
                        <Button
                            size="small"
                            onClick={() => setAllColumnsVisible(true)}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#304FFF' }}
                        >
                            {t('ShowAll')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setAllColumnsVisible(false)}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#696665' }}
                        >
                            {t('HideAll')}
                        </Button>
                    </Box>
                </Popover>
                {isCheckboxSelectionEnabled && selectionCount > 0 && (
                    <Box className={styles.toolbarEnd}>
                        <Box className={styles.selectionBar}>
                            <Typography variant="body2" sx={{ color: '#1A1F49', fontWeight: 600, fontSize: '0.875rem' }}>
                                {t('Selected')}:
                            </Typography>
                            <Chip
                                label={selectionCount}
                                size="small"
                                sx={{
                                    backgroundColor: '#304FFF',
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    height: '22px',
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
            <Box ref={fitContainerRef} sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <StyledDataGrid
                    key={tableView}
                    className={fitEnabled ? 'fit-to-screen' : undefined}
                    apiRef={apiRef}
                    rows={rows}
                    columns={columnsWithWidths}
                    onColumnWidthChange={handleColumnWidthChange}
                    checkboxSelection={isCheckboxSelectionEnabled}
                    disableRowSelectionOnClick
                    columnHeaderHeight={42}
                    getRowHeight={() => (fitEnabled ? 48 : 'auto')}
                    getEstimatedRowHeight={() => (fitEnabled ? 48 : 72)}
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={handleSelectionModelChange}
                    getRowId={(row) => row.id}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                    pagination
                    sortingOrder={['asc', 'desc']}
                    processRowUpdate={processRowUpdate}
                    experimentalFeatures={{ newEditingApi: true }}
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={setRowModesModel}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
                    onCellDoubleClick={handleCellDoubleClick}
                    sx={{ height: '100%' }}
                />
            </Box>
        </div>
    );
});

export default OtherDocumentDataGrid;
